// apps/web/src/features/crm/leads/card/ArtifactsBlock.tsx
//
// The lead card's single "Artifacts" `SectionCard` (#2316; vendored
// reference `design-refs/crm/lead/lead-sidebar.jsx:161-218`), replacing the
// former separate Files + Intake `<section>`s in `LeadCardSidebar.tsx`: an
// "In system" subsection of linked requests/deals/intake/CP, a "Files"
// subsection, and an overflow header menu with "File" (manager-only) and
// "CP" (#2318, owner-only — see `canCreateProposal`) items. CP rows
// themselves render/navigate via `ArtifactsSystemSection`.
//
// D-4 ROLLBACK-DEGRADE: `GET /leads/:id/artifacts` is a brand-new endpoint
// (#2314). A new-web→old-backend rollback 404s it while the card is still
// visible. `artifactsQ`'s pending/error state both fold into an empty
// `entities` array below — "In system" just shows fewer rows instead of
// crashing, replacing the card, or hiding the (unaffected, separately
// queried) "Files"/intake subsections.
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, createSearchParams } from 'react-router';
import { LEAD_FILE_ALLOWED_MIME, LEAD_FILE_MAX_BYTES } from '@magimp/shared';
import { useLeadArtifacts, useLeadFiles, useLeadIntakeHistory } from '@magimp/shared-hooks';
import { IconButton, Menu, MenuItem, SectionCard } from '../../../../ui/index.js';
import { useLeadFileUpload } from '../activity/useLeadFileUpload.js';
import { ArtifactsSystemSection, isRenderableArtifactKind } from './ArtifactsSystemSection.js';
import { ArtifactsFilesSection } from './ArtifactsFilesSection.js';

const UPLOAD_ACCEPT = LEAD_FILE_ALLOWED_MIME.join(',');

export interface ArtifactsBlockProps {
  leadId: string;
  canManage: boolean;
  /** D-1 (#2318): owner-only CP-create gate, computed by the page. */
  canCreateProposal: boolean;
  /** D-2 (#2318): convenience client-name seed for the CP create form. */
  clientPrefill: string;
}

export function ArtifactsBlock({
  leadId,
  canManage,
  canCreateProposal,
  clientPrefill,
}: ArtifactsBlockProps) {
  const { t } = useTranslation(['leads']);
  const navigate = useNavigate();
  const artifactsQ = useLeadArtifacts(leadId);
  const filesQ = useLeadFiles(leadId);
  const intakeQ = useLeadIntakeHistory(leadId);
  const inputRef = useRef<HTMLInputElement>(null);

  const maxBytes = filesQ.data?.usage.maxBytesPerFile ?? LEAD_FILE_MAX_BYTES;
  const { upload, uploading } = useLeadFileUpload(leadId, { maxBytes });

  const capReached = !!filesQ.data && filesQ.data.usage.count >= filesQ.data.usage.maxPerLead;
  const addFileDisabled = filesQ.isPending || !!filesQ.error || capReached || uploading;

  // Combined empty copy (D-1) only once files + intake have DEFINITELY
  // resolved with zero items and there is no known system entity either.
  // `artifactsQ`'s own pending/error deliberately does not gate this (same
  // degrade-first posture as the rollback case): an unresolved artifacts
  // query just contributes "no known entities yet", which self-corrects the
  // moment the query settles.
  const filesEmpty = filesQ.isSuccess && filesQ.data.items.length === 0;
  const intakeEmpty = intakeQ.isSuccess && intakeQ.data.items.length === 0;
  const noKnownEntities = !(artifactsQ.data?.entities ?? []).some((e) =>
    isRenderableArtifactKind(e.kind),
  );
  const showEmptyCopy = filesEmpty && intakeEmpty && noKnownEntities;

  return (
    <div data-testid="lead-card-artifacts">
      <SectionCard
        title={t('leads:card.artifacts.title')}
        action={
          canManage || canCreateProposal ? (
            <Menu
              trigger={(triggerProps) => (
                <IconButton
                  {...triggerProps}
                  icon="more-vertical"
                  label={t('leads:card.artifacts.menuLabel')}
                />
              )}
            >
              {canManage && (
                <MenuItem disabled={addFileDisabled} onClick={() => inputRef.current?.click()}>
                  <span data-testid="lead-artifacts-add-file">
                    {t('leads:card.artifacts.addFile')}
                  </span>
                </MenuItem>
              )}
              {canCreateProposal && (
                <MenuItem
                  onClick={() =>
                    // D-2 (#2318, PII-SAFE): the query string carries ONLY
                    // `leadId` — client/contact display text is PII and
                    // travels in router STATE, never the URL.
                    navigate(
                      {
                        pathname: '/kp/new',
                        search: createSearchParams({ leadId }).toString(),
                      },
                      { state: { clientPrefill } },
                    )
                  }
                >
                  <span data-testid="lead-artifacts-add-proposal">
                    {t('leads:card.artifacts.addProposal')}
                  </span>
                </MenuItem>
              )}
            </Menu>
          ) : undefined
        }
      >
        {canManage && (
          <input
            ref={inputRef}
            type="file"
            accept={UPLOAD_ACCEPT}
            disabled={uploading}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (file) void upload(file);
            }}
            style={{ display: 'none' }}
            data-testid="lead-files-input"
          />
        )}
        {showEmptyCopy && (
          <div style={{ fontSize: 13, color: 'var(--fg-3)' }} data-testid="lead-artifacts-empty">
            {t('leads:card.artifacts.empty')}
          </div>
        )}
        <ArtifactsSystemSection
          entities={artifactsQ.data?.entities ?? []}
          intakeItems={intakeQ.data?.items ?? []}
          intakePending={intakeQ.isPending}
          intakeError={intakeQ.error}
          onRetryIntake={() => void intakeQ.refetch()}
        />
        <ArtifactsFilesSection leadId={leadId} canManage={canManage} />
      </SectionCard>
    </div>
  );
}
